import React from "react";
import {
  Box,
  Button,
  Container,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import { Link as RouterLink } from "react-router-dom";
import colors from "../assets/theme/colors";
import { landingPageStyles } from "../assets/theme/landingTheme";
import Footer from "../components/Landing/Footer";
import BrandWordmark from "../components/UI/BrandWordmark";
import brandConfig from "../shared/brandConfig.json";

const UPDATED_DATE = "June 6, 2026";

const KEY_POINTS = [
  "Lifelog is a caregiver documentation and coordination tool, not a medical, legal, or emergency service.",
  "Care information should be entered only when you have the right authority or permission.",
  "AI-assisted organization can help reduce manual sorting, but users remain responsible for reviewing important details.",
  "You are responsible for keeping your account secure and inviting only appropriate care team members.",
];

const TERMS_SECTIONS = [
  {
    title: "1. Agreement to these terms",
    body: [
      "These Terms of Service apply when you visit lifelog.care, create an account, or use Lifelog and related services.",
      "By using Lifelog, you agree to these terms and to our Privacy Policy. If you do not agree, please do not use the service.",
      "We may update these terms from time to time. The updated version will show a new updated date, and continued use of Lifelog means you accept the updated terms.",
    ],
  },
  {
    title: "2. What Lifelog provides",
    body: [
      "Lifelog is a caregiver logging and coordination platform that helps families and care teams capture observations, behaviors, sleep, medications, routines, documents, and care notes in a shared timeline.",
      "The service may include AI-assisted tools that help organize text, classify entries, extract possible care details, and surface care insights.",
      "Lifelog is designed to support documentation and coordination. It does not replace professional judgment, medical advice, legal advice, therapy, diagnosis, treatment, or emergency services.",
    ],
  },
  {
    title: "3. Accounts and eligibility",
    body: [
      "You must be able to form a binding agreement and be at least 18 years old, or the equivalent age required by law in your jurisdiction, to create an account.",
      "You are responsible for providing accurate account information, keeping it current, and protecting your sign-in credentials.",
      "You are responsible for all activity that occurs through your account unless you promptly notify us of unauthorized access and we determine otherwise.",
    ],
  },
  {
    title: "4. Care profiles, invitations, and permissions",
    body: [
      "Care Owners and authorized users can create care profiles, invite care team members, and set or manage access according to available role and permission settings.",
      "You should invite only people who have an appropriate reason to view or contribute care information.",
      "If you add information about a child, family member, patient, student, or another person, you are responsible for having the right authority, consent, or permission to do so.",
      "Lifelog is not responsible for choices you make about who you invite, what access you grant, or what information you choose to share inside a care circle.",
    ],
  },
  {
    title: "5. User content and care information",
    body: [
      "You keep ownership of the notes, documents, photos, observations, and other information you enter or upload to Lifelog.",
      "You give us permission to host, store, process, display, organize, back up, and transmit your content as needed to provide, secure, support, and improve the service.",
      "You are responsible for the accuracy, legality, and appropriateness of the information you enter, upload, import, or share through Lifelog.",
      "You should review important details before relying on them, especially medication, medical, school, therapy, legal, financial, or safety-related information.",
    ],
  },
  {
    title: "6. AI-assisted features",
    body: [
      "Lifelog may use AI-assisted features to organize care notes, classify entries, extract text, summarize details, and suggest timeline information.",
      "AI output may be incomplete, incorrect, outdated, or unsuitable for your situation. You are responsible for reviewing AI-assisted output before using it.",
      "AI-assisted features do not provide medical advice, diagnosis, treatment recommendations, legal advice, or emergency guidance.",
      "Do not use AI-assisted features in a way that violates applicable law, these terms, or the terms and policies of any AI service provider used to support the feature.",
    ],
  },
  {
    title: "7. Acceptable use",
    body: [
      "Do not use Lifelog for unlawful, harmful, abusive, deceptive, invasive, or unauthorized purposes.",
      "Do not upload malware, attempt to bypass security, disrupt the service, scrape the service, reverse engineer restricted parts of the service, or access accounts or data you are not authorized to access.",
      "Do not use Lifelog to harass, exploit, threaten, or harm another person, or to share information you do not have permission to share.",
      "Do not use Lifelog as an emergency response system. In an emergency, contact local emergency services or an appropriate professional directly.",
    ],
  },
  {
    title: "8. Third-party services",
    body: [
      "Lifelog may rely on third-party providers for hosting, authentication, databases, storage, AI processing, crash reporting, analytics, email, and other infrastructure or support services.",
      "Your use of sign-in providers, connected accounts, device platforms, app stores, or external services may also be subject to those third parties' terms and policies.",
      "We are not responsible for third-party services that we do not control, even if they are linked from or integrated with Lifelog.",
    ],
  },
  {
    title: "9. Privacy",
    body: [
      "Our Privacy Policy explains how we collect, use, share, retain, and protect information when you use Lifelog.",
      "By using Lifelog, you acknowledge that care information may include sensitive personal information and that you are responsible for entering and sharing it appropriately.",
      "You can review the Privacy Policy at /privacy-policy.",
    ],
  },
  {
    title: "10. Service changes and availability",
    body: [
      "We may add, change, suspend, limit, or remove parts of Lifelog as the service evolves.",
      "We aim to keep Lifelog reliable, but we do not guarantee that the service will always be available, uninterrupted, secure, or error-free.",
      "We may suspend or restrict access if needed for security, maintenance, legal compliance, misuse prevention, or service integrity.",
    ],
  },
  {
    title: "11. Account termination",
    body: [
      "You may stop using Lifelog at any time and may contact us for help with account or data requests.",
      "We may suspend or terminate access if we believe you violated these terms, created risk, misused the service, infringed rights, or used the service unlawfully.",
      "After account closure, some information may remain where needed for backups, legal obligations, safety, security, dispute resolution, or legitimate operational purposes.",
    ],
  },
  {
    title: "12. Intellectual property",
    body: [
      "Lifelog, including its software, design, branding, features, and content we provide, is owned by us or our licensors and is protected by intellectual property laws.",
      "These terms do not give you ownership of Lifelog or permission to copy, modify, distribute, sell, or exploit the service except as allowed by these terms.",
      "Feedback or suggestions you provide may be used by us without restriction or compensation to you.",
    ],
  },
  {
    title: "13. Disclaimers",
    body: [
      "Lifelog is provided as is and as available, without warranties of any kind to the fullest extent permitted by law.",
      "We do not warrant that care records, AI-assisted output, summaries, classifications, imports, or insights will be accurate, complete, timely, or suitable for any particular purpose.",
      "You are responsible for confirming important information with qualified professionals, original sources, or appropriate care team members.",
    ],
  },
  {
    title: "14. Limitation of liability",
    body: [
      "To the fullest extent permitted by law, we will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages, or for lost profits, lost data, or loss of goodwill.",
      "Our total liability for claims relating to the service will be limited to the amount you paid us for the service during the twelve months before the claim, or one hundred dollars if you have not paid us.",
      "Some jurisdictions do not allow certain limits, so some of these limits may not apply to you.",
    ],
  },
  {
    title: "15. Indemnity",
    body: [
      "You agree to defend, indemnify, and hold us harmless from claims, damages, liabilities, costs, and expenses arising from your use of Lifelog, your content, your invitations or sharing choices, or your violation of these terms or applicable law.",
      "This obligation applies to the extent permitted by law.",
    ],
  },
  {
    title: "16. Governing law",
    body: [
      "These terms are governed by the laws of the United States and the State of Texas, without regard to conflict-of-law rules, unless applicable law requires otherwise.",
      "If a dispute cannot be resolved informally, it will be handled in the courts located in Texas unless applicable law gives you other rights.",
    ],
  },
  {
    title: "17. Contact",
    body: [
      "Questions about these terms can be sent to the contact email below.",
      "We may need to verify your identity or account access before helping with account-specific requests.",
    ],
  },
];

const TermsOfServicePage = () => (
  <Box sx={landingPageStyles} data-cy="terms-of-service-page">
    <Box
      sx={{
        borderBottom: `1px solid ${colors.landing.borderSoft}`,
        bgcolor: alpha(colors.semantic.surface, 0.78),
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2.25, md: 4 } }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ minHeight: { xs: 72, md: 82 } }}
        >
          <Button
            component={RouterLink}
            to="/"
            aria-label="Lifelog home"
            sx={{
              p: 0,
              minWidth: "auto",
              textTransform: "none",
              "&:hover": { bgcolor: "transparent" },
            }}
          >
            <BrandWordmark variant="navbar" showDot={false} />
          </Button>
          <Button
            component={RouterLink}
            to="/"
            startIcon={<ChevronLeftRoundedIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 800,
              color: colors.semantic.ink,
              borderRadius: "999px",
              px: 1.6,
            }}
          >
            Back to home
          </Button>
        </Stack>
      </Container>
    </Box>

    <Container maxWidth="lg" sx={{ px: { xs: 2.25, md: 4 }, py: { xs: 4, md: 6 } }}>
      <Box
        sx={{
          p: { xs: 2.4, md: 4 },
          borderRadius: "28px",
          bgcolor: colors.semantic.surface,
          border: `1px solid ${colors.landing.borderLight}`,
          boxShadow: `0 20px 54px ${alpha(colors.semantic.ink, 0.06)}`,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.78rem",
            fontWeight: 850,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: colors.semantic.sageDeep,
          }}
        >
          Terms of Service
        </Typography>
        <Typography
          component="h1"
          sx={{
            mt: 1,
            fontFamily: "'Outfit', sans-serif",
            fontSize: { xs: "2.35rem", md: "3.1rem" },
            lineHeight: 1.05,
            fontWeight: 850,
            color: colors.landing.heroText,
            letterSpacing: 0,
          }}
        >
          The ground rules for using Lifelog.
        </Typography>
        <Typography
          sx={{
            mt: 1.5,
            maxWidth: 780,
            color: colors.landing.textMuted,
            fontSize: { xs: "1rem", md: "1.08rem" },
            lineHeight: 1.65,
          }}
        >
          These terms explain how Lifelog may be used, what users are responsible for, and the limits of the service.
        </Typography>
        <Typography sx={{ mt: 1.5, color: colors.landing.textSoft, fontWeight: 750 }}>
          Last updated: {UPDATED_DATE}
        </Typography>

        <Box
          sx={{
            mt: 3,
            p: { xs: 1.8, md: 2.2 },
            borderRadius: "22px",
            bgcolor: colors.brand.tint,
            border: `1px solid ${colors.landing.borderLight}`,
          }}
        >
          <Typography sx={{ fontWeight: 900, color: colors.semantic.ink, fontSize: "1.2rem" }}>
            At a glance
          </Typography>
          <Stack component="ul" spacing={0.8} sx={{ mt: 1.1, pl: 2.5, mb: 0 }}>
            {KEY_POINTS.map((item) => (
              <Typography component="li" key={item} sx={{ color: colors.landing.textMuted, lineHeight: 1.62, pl: 0.4 }}>
                {item}
              </Typography>
            ))}
          </Stack>
        </Box>

        <Stack spacing={2.2} sx={{ mt: { xs: 3, md: 4 } }}>
          {TERMS_SECTIONS.map((section) => (
            <Box
              key={section.title}
              sx={{
                p: { xs: 1.8, md: 2.2 },
                borderRadius: "20px",
                bgcolor: alpha(colors.semantic.sageMist, 0.5),
                border: `1px solid ${colors.landing.borderLight}`,
              }}
            >
              <Typography component="h2" sx={{ fontSize: { xs: "1.25rem", md: "1.42rem" }, fontWeight: 900, color: colors.semantic.ink }}>
                {section.title}
              </Typography>
              <Stack component="ul" spacing={0.8} sx={{ mt: 1.1, pl: 2.5, mb: 0 }}>
                {section.body.map((item) => (
                  <Typography component="li" key={item} sx={{ color: colors.landing.textMuted, lineHeight: 1.62, pl: 0.4 }}>
                    {item}
                  </Typography>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>

        <Box
          sx={{
            mt: 3,
            p: { xs: 2, md: 2.4 },
            borderRadius: "22px",
            bgcolor: colors.brand.tint,
            border: `1px solid ${colors.landing.borderLight}`,
          }}
        >
          <Typography sx={{ fontWeight: 900, color: colors.semantic.ink, fontSize: "1.2rem" }}>
            Questions
          </Typography>
          <Typography sx={{ mt: 0.7, color: colors.landing.textMuted, lineHeight: 1.62 }}>
            Contact us at{" "}
            <Link href={`mailto:${brandConfig.supportEmail}`} sx={{ color: colors.brand.deep, fontWeight: 850 }}>
              {brandConfig.supportEmail}
            </Link>
            .
          </Typography>
        </Box>
      </Box>
    </Container>

    <Footer variant="desktop" />
  </Box>
);

export default TermsOfServicePage;
